import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { login as apiLogin } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDatabase,
  faEye,
  faEyeSlash,
  faMoon,
  faSun,
  faBolt,
  faBrain,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';



export default function LoginPage() {
  const { login } = useAuth();
  const { toggle, isDark } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error('Fill in all fields');
    setLoading(true);
    try {
      const data = await apiLogin({ email: form.email, password: form.password });
      login(data.access_token, data.user);
      toast.success(`Welcome back, ${data.user.full_name || data.user.email}!`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={s.page}>
      {/* Left branding panel */}
      <div style={s.left}>
        <div style={s.leftContent}>
          <div style={s.brand}>
            <span style={s.brandMark}><FontAwesomeIcon icon={faDatabase} aria-hidden="true" /></span>
            <span style={s.brandName}>DataSphere</span>
          </div>
          <h1 style={s.headline}>Your data,<br />answered instantly.</h1>
          <p style={s.sub}>Connect any database or file. Ask questions in plain English. Get SQL, tables, and charts — powered by AI.</p>
          <div style={s.featureGrid}>
  <div style={s.featureCard}>
    <FontAwesomeIcon icon={faBolt} style={s.featureIcon} />
    <div>
      <div style={s.featureTitle}>Instant Insights</div>
      <div style={s.featureDesc}>Query and analyze data in real time</div>
    </div>
  </div>

  <div style={s.featureCard}>
    <FontAwesomeIcon icon={faBrain} style={s.featureIcon} />
    <div>
      <div style={s.featureTitle}>AI-Powered Analysis</div>
      <div style={s.featureDesc}>Generate intelligent queries automatically</div>
    </div>
  </div>

  <div style={s.featureCard}>
    <FontAwesomeIcon icon={faChartLine} style={s.featureIcon} />
    <div>
      <div style={s.featureTitle}>Visual Dashboards</div>
      <div style={s.featureDesc}>Transform results into clean visual reports</div>
    </div>
  </div>
</div>
        </div>
        <div style={s.orb1} />
        <div style={s.orb2} />
      </div>

      {/* Right form panel */}
      <div style={s.right}>
        {/* <button onClick={toggle} style={s.themeBtn} aria-label="Toggle theme">
          <FontAwesomeIcon icon={isDark ? faSun : faMoon} aria-hidden="true" />
        </button> */}

        <div style={s.formCard}>
          <h2 style={s.formTitle}>Sign in</h2>
          <p style={s.formSub}>Enter your credentials to access your workspace</p>

          <form onSubmit={handleSubmit} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Email address</label>
              <input
                style={s.input}
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={e => set('email', e.target.value)}
                autoFocus
                disabled={loading}
              />
            </div>

            <div style={s.field}>
              <div style={s.labelRow}>
                <label style={s.label}>Password</label>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...s.input, paddingRight: 44 }}
                  type={showPass ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  disabled={loading}
                />
                <button type="button" onClick={() => setShowPass(p => !p)} style={s.eyeBtn} aria-label={showPass ? 'Hide password' : 'Show password'}>
                  <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} aria-hidden="true" />
                </button>
              </div>
            </div>

            <button
              type="submit"
              style={{ ...s.submitBtn, ...(loading ? s.btnLoading : {}) }}
              disabled={loading}
            >
              {loading
                ? <><span style={s.spinner} /> Signing in…</>
                : 'Sign in →'}
            </button>
          </form>

          <div style={s.divider}><span>New to DataSphere?</span></div>

          <Link to="/register" style={s.registerLink}>
            Create a free account
          </Link>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    display: 'flex', height: '100vh', overflow: 'hidden',
    background: 'var(--bg-primary)',
  },
left: {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '80px 100px', // main control
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, rgb(41 117 244) 0%, rgb(109 108 223) 50%, rgb(171 191 236) 100%)',
},
leftContent: {
  width: '100%',
  maxWidth: 'none', // important
},
  brand: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 },
  brandMark: { fontSize: 32, color: '#fff' },
  brandName: { fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' },
  headline: {
  maxWidth: 650,
  fontSize: 48,
  fontWeight: 800,
  color: '#fff',
  lineHeight: 1.1,
  marginBottom: 20,
},

sub: {
  maxWidth: 520,
  fontSize: 16,
  color: 'rgba(255,255,255,0.85)',
  lineHeight: 1.7,
  marginBottom: 40,
},
  features: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  feature: {
    padding: '6px 14px', borderRadius: 20, background: 'rgba(255,255,255,0.18)',
    color: '#fff', fontSize: 13, fontWeight: 500, backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255,255,255,0.25)',
  },
  orb1: {
    position: 'absolute', width: 400, height: 400, borderRadius: '50%',
    background: 'rgba(255,255,255,0.07)', top: -100, right: -100,
  },
  orb2: {
    position: 'absolute', width: 300, height: 300, borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)', bottom: -80, left: -60,
  },
 right: {
  width: 480,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 40,
  position: 'relative',
  background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
  borderLeft: '1px solid rgba(0,0,0,0.04)',
},
  themeBtn: {
    position: 'absolute', top: 20, right: 20, fontSize: 20,
    background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
    borderRadius: 10, width: 40, height: 40, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  formCard: {
  width: '100%',
  maxWidth: 380,
  padding: '32px 28px',
  borderRadius: 20,
  background: 'rgba(255,255,255,0.75)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.6)',
  boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
  animation: 'slideUp 0.35s ease',
},
  formTitle: {
  fontSize: 28,
  fontWeight: 800,
  marginBottom: 6,
},

formSub: {
  fontSize: 14,
  color: 'var(--text-muted)',
  marginBottom: 26,
},
  form: { display: 'flex', flexDirection: 'column', gap: 18 },
  field: { display: 'flex', flexDirection: 'column', gap: 7 },
  label: { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  input: {
  padding: '13px 14px',
  borderRadius: 12,
  border: '1.5px solid rgba(0,0,0,0.08)',
  background: '#fff',
  fontSize: 14,
  outline: 'none',
  width: '100%',
  transition: 'all 0.2s ease',
},
inputFocus: {
  borderColor: '#6366f1',
  boxShadow: '0 0 0 3px rgba(99,102,241,0.15)',
},
  eyeBtn: {
    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 4,
  },
 submitBtn: {
  marginTop: 8,
  padding: '14px',
  borderRadius: 14,
  background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
  color: '#fff',
  fontWeight: 700,
  fontSize: 15,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  transition: 'all 0.25s ease',
  boxShadow: '0 10px 25px rgba(79,70,229,0.35)',
},
submitHover: {
  transform: 'translateY(-1px)',
  boxShadow: '0 14px 30px rgba(79,70,229,0.4)',
},
  btnLoading: { opacity: 0.7, cursor: 'not-allowed' },
  spinner: {
    width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0 16px',
    color: 'var(--text-muted)', fontSize: 12,
  },
  registerLink: {
    display: 'block', textAlign: 'center', padding: '12px',
    borderRadius: 12, border: '1.5px solid var(--border)',
    color: 'var(--text-primary)', fontWeight: 600, fontSize: 14,
    transition: 'all 0.2s', background: 'var(--bg-primary)',
  },
  featureGrid: {
  width: '100%',
  maxWidth: 600,
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
},

featureCard: {
  width: '100%',
  display: 'flex',
  gap: 14,
  padding: '16px 18px',
  borderRadius: 16,
  background: 'rgba(255,255,255,0.10)',
  border: '1px solid rgba(255,255,255,0.18)',
  backdropFilter: 'blur(12px)',
  alignItems: 'flex-start',
  transition: 'all 0.25s ease',
},

featureIcon: {
  fontSize: 16,
  color: '#fff',
  marginTop: 3,
  opacity: 0.9,
},

featureTitle: {
  fontSize: 14,
  fontWeight: 700,
  color: '#fff',
  marginBottom: 4,
},

featureDesc: {
  fontSize: 12.5,
  color: 'rgba(255,255,255,0.75)',
  lineHeight: 1.5,
},
};
